// Centralized editable configuration for Premier Global Sourcing.
// All company, contact, service, market and content data lives here so it can be edited easily.

export const company = {
  name: "Premier Global Sourcing",
  legalName: "Premier Global Sourcing (Private) Limited",
  tagline: "Worldwide Trading, Sourcing and Logistics Coordination",
  domain: "www.globalsourcingltd.com",
  registration: "[COMPANY REGISTRATION NUMBER — TO BE ADDED]",
};

export const contact = {
  address: "Karachi, Pakistan",
  locations: ["Karachi, Pakistan", "Dubai, UAE"],
  phone: "+92 335 2261070",
  phoneHref: "tel:+923352261070",
  whatsapp: "+44 7544 982845",
  whatsappNumber: "447544982845", // used for wa.me deep link
  email: "info@globalsourcingltd.com",
  openingHours: "Monday to Saturday, 24 hours",
  mapsEmbed: "", // [ADD VERIFIED MAP EMBED URL]
  googleBusiness: "", // [ADD VERIFIED PROFILE LINK]
};

export const socials = [
  { label: "LinkedIn", href: "#", handle: "[ADD PROFILE]" },
  { label: "Facebook", href: "#", handle: "[ADD PROFILE]" },
  { label: "Instagram", href: "#", handle: "[ADD PROFILE]" },
];

export const waLink = (msg = "Hello Premier Global Sourcing, I'd like to discuss a sourcing requirement.", number) =>
  `https://wa.me/${(number || contact.whatsappNumber || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;

export const services = [
  {
    slug: "sourcing-services",
    icon: "Search",
    title: "Product Sourcing",
    short: "Research suitable sourcing options based on your product, specifications, quantity and target market.",
    copy: "Share the product, specifications, quantity and target market. We will review the requirement and research suitable sourcing options for further evaluation.",
    path: "/sourcing-services",
  },
  {
    slug: "supplier-coordination",
    icon: "Network",
    title: "Supplier Coordination",
    short: "Organize supplier communication, quotations, product details, samples and production information.",
    copy: "Organize supplier communication, quotations, product details, samples and production-related information.",
    path: "/sourcing-services#supplier-coordination",
  },
  {
    slug: "trading-services",
    icon: "ArrowLeftRight",
    title: "International Trading",
    short: "Coordinate commercial requirements between buyers and sellers in different markets.",
    copy: "Coordinate commercial requirements between buyers and sellers in different markets.",
    path: "/trading-services",
  },
  {
    slug: "logistics-coordination",
    icon: "Route",
    title: "Logistics Coordination",
    short: "Evaluate available air, sea or land transport options with relevant service providers.",
    copy: "Coordinate with relevant service providers to evaluate available air, sea or land transport options based on the cargo, origin and destination.",
    path: "/logistics-coordination",
  },
  {
    slug: "custom-procurement",
    icon: "Crosshair",
    title: "Custom Procurement",
    short: "A focused supplier and market search for unusual, specialized or hard-to-locate requirements.",
    copy: "Conduct a focused supplier and market search for unusual, specialized or hard-to-locate requirements.",
    path: "/sourcing-services#custom-procurement",
  },
  {
    slug: "sample-coordination",
    icon: "Package",
    title: "Sample Coordination",
    short: "Coordinate sample requests where available before larger commercial orders are considered.",
    copy: "Coordinate sample requests where available before larger commercial orders are considered.",
    path: "/sourcing-services#sample-coordination",
  },
];

export const markets = [
  { code: "PK", name: "Pakistan", role: "Home base & coordination", note: "International trade team coordinating enquiries.", x: 66, y: 45 },
  { code: "CN", name: "China", role: "Major sourcing market", note: "Manufacturing and product sourcing coordination.", x: 78, y: 42 },
  { code: "AE", name: "UAE", role: "Trade & re-export hub", note: "Regional commercial and trade coordination.", x: 62, y: 47 },
  { code: "SA", name: "Saudi Arabia", role: "Commercial market", note: "Buyer and supplier coordination.", x: 59, y: 48 },
  { code: "IR", name: "Iran", role: "Regional market", note: "Commercial coordination where applicable.", x: 61, y: 44 },
  { code: "US", name: "USA", role: "Commercial market", note: "Buyer and supplier coordination.", x: 20, y: 42 },
  { code: "EU", name: "Europe", role: "Commercial market", note: "Buyer and supplier coordination across selected countries.", x: 48, y: 36 },
];

export const industries = [
  { icon: "Store", title: "Retail and wholesale", note: "Editable" },
  { icon: "ShoppingCart", title: "E-commerce and private label", note: "Editable" },
  { icon: "Factory", title: "Industrial supplies", note: "Editable" },
  { icon: "Cog", title: "Machinery and spare parts", note: "Editable" },
  { icon: "HardHat", title: "Construction materials", note: "Editable" },
  { icon: "UtensilsCrossed", title: "Hospitality supplies", note: "Editable" },
  { icon: "Boxes", title: "Consumer products", note: "Editable" },
];

export const processSteps = [
  { n: "01", title: "Share Your Requirement", copy: "Send the product details, specifications, quantity and destination through the quote form or WhatsApp." },
  { n: "02", title: "Requirement Review", copy: "Our team reviews the request and confirms the information needed to move forward." },
  { n: "03", title: "Sourcing and Options", copy: "We research suitable sourcing options and supplier possibilities for your evaluation." },
  { n: "04", title: "Confirmation and Coordination", copy: "Once a direction is agreed, we coordinate supplier communication and commercial details." },
  { n: "05", title: "Logistics Planning", copy: "We evaluate available transport options with relevant service providers based on the cargo and route." },
  { n: "06", title: "Updates and Handover", copy: "We keep you updated with clear next steps through coordination and handover." },
];

export const whyChoose = [
  { icon: "MessagesSquare", title: "One coordinated conversation", copy: "Keep sourcing, trading and logistics discussions in a single, organized thread." },
  { icon: "ClipboardList", title: "Requirement-first approach", copy: "We start from your specifications, not a fixed catalogue." },
  { icon: "Globe2", title: "Access to selected international markets", copy: "Coordination across Asia, the Middle East, North America and Europe." },
  { icon: "SlidersHorizontal", title: "Flexible project support", copy: "Support scaled to the size and complexity of your requirement." },
  { icon: "ListChecks", title: "Clearer next steps", copy: "You always know what information or action is required next." },
  { icon: "Handshake", title: "Practical supplier and logistics coordination", copy: "Connecting suppliers and transport planning in one place." },
];

export const credibility = [
  "B2B Sourcing Support",
  "Multi-Market Coordination",
  "Product-to-Delivery Assistance",
  "International Trade Team",
];

export const faqs = [
  { q: "What information should I send for a sourcing enquiry?", a: "Share the product name, a clear description, technical specifications, target quantity, preferred sourcing country and delivery destination. The more detail you provide, the faster we can review the requirement." },
  { q: "Can you source customized or branded products?", a: "Yes. Send the specifications and any branding or private-label requirements, and we will research suitable sourcing options for your evaluation." },
  { q: "Which countries do you work with?", a: "We coordinate enquiries involving selected markets including Pakistan, China, UAE, Saudi Arabia, Iran, USA and Europe. Service areas shown do not necessarily represent company-owned offices." },
  { q: "Do you provide shipping?", a: "We coordinate with relevant service providers to evaluate available air, sea or land transport options. Freight and related services may be arranged through qualified partners where applicable." },
  { q: "Do you handle customs clearance?", a: "Customs and other regulated services may be coordinated through relevant qualified partners where applicable. We do not represent ourselves as a licensed customs broker." },
  { q: "Can you guarantee product quality?", a: "We coordinate sample requests and supplier information where available so you can evaluate options. We do not offer blanket quality guarantees." },
  { q: "How long does sourcing take?", a: "Timelines depend on the product, quantity and market. After reviewing your requirement, we will explain the expected next steps and information needed." },
  { q: "What is the minimum order quantity?", a: "Minimums vary by product and supplier. Share your target quantity and we will review suitable options accordingly." },
  { q: "How are payments handled?", a: "Commercial and payment terms are discussed and agreed as part of coordination for each specific requirement." },
  { q: "How can I request a quotation?", a: "Use the Request a Quote form with your product details, quantity and destination, or contact us on WhatsApp. We will review and respond with the next steps." },
];

export const serviceOptions = [
  "Product Sourcing",
  "Supplier Coordination",
  "International Trading",
  "Logistics Coordination",
  "Custom Procurement",
  "Sample Coordination",
  "Not sure yet",
];

export const countryOptions = [
  "Pakistan", "China", "UAE", "Saudi Arabia", "Iran", "USA",
  "United Kingdom", "Germany", "France", "Other European country", "Other",
];

export const incotermOptions = ["EXW", "FOB", "CIF", "CFR", "DDP", "DAP", "Not sure"];
export const unitOptions = ["units (pcs)", "kg", "tonnes", "m²", "m³", "metres", "litres", "pairs", "sets", "cartons", "containers"];
export const contactMethods = ["Email", "WhatsApp", "Phone call"];
export const timelineOptions = ["Urgent (within 2 weeks)", "1–2 months", "3–6 months", "Flexible / planning ahead"];

export const logisticsSolutions = [
  { icon: "Ship", title: "Ocean freight coordination", copy: "FCL and LCL options coordinated with carrier and forwarder partners across major trade lanes." },
  { icon: "Plane", title: "Air freight coordination", copy: "Faster options for time-sensitive or high-value cargo, evaluated with relevant providers." },
  { icon: "Truck", title: "Inland & land transport", copy: "Road and rail movement connecting ports to inland origins and destinations." },
  { icon: "Boxes", title: "Project & awkward cargo", copy: "Coordination for oversized, heavy or non-standard shipments requiring special handling." },
  { icon: "Snowflake", title: "Reefer & perishables", copy: "Temperature-sensitive cargo coordination with suitable equipment where available." },
  { icon: "FileCheck", title: "Documentation & customs support", copy: "Customs and documentation arranged through qualified partners where applicable." },
];

export const cargoNatureOptions = ["General", "Refrigerated", "Awkward / Oversized", "Dangerous cargo"];
export const loadingTerms = ["CY (Container Yard)", "DOOR"];
export const freightModes = ["Sea — FCL", "Sea — LCL", "Air freight", "Land transport", "Not sure"];
export const containerTypes = ["1 x 20GP", "1 x 40GP", "1 x 40HQ", "2 x 40HQ", "LCL (loose)", "Other / not sure"];

export const nav = [
  { label: "Home", path: "/" },
  {
    label: "Services",
    path: "/sourcing-services",
    children: [
      { label: "Product Sourcing", path: "/sourcing-services" },
      { label: "Trading Services", path: "/trading-services" },
      { label: "Logistics Coordination", path: "/logistics-coordination" },
      { label: "Freight Rate Enquiry", path: "/rate-quote" },
      { label: "Track & Schedules", path: "/track" },
    ],
  },
  { label: "Markets", path: "/markets" },
  { label: "Industries", path: "/industries" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "About", path: "/about" },
  { label: "Insights", path: "/insights" },
  { label: "Contact", path: "/contact" },
];
